import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  connectEmail, 
  getEmailSettings, 
  deleteEmailAccount, 
  setEmailPrimary,
  syncEmails 
} from '../lib/api';
import { 
  Mail, 
  Plus, 
  Trash2, 
  Star, 
  RefreshCw, 
  Loader2, 
  CheckCircle, 
  XCircle,
  Eye,
  EyeOff,
  Server,
  Lock,
  Globe,
} from 'lucide-react';

export default function EmailAccountsManager() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [settingPrimaryId, setSettingPrimaryId] = useState(null);
  
  // New account form
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newAccountName, setNewAccountName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [adding, setAdding] = useState(false);
  
  // Server settings
  const [imapServer, setImapServer] = useState('imap.hostinger.com');
  const [smtpServer, setSmtpServer] = useState('smtp.hostinger.com');
  const [imapPort, setImapPort] = useState('993');
  const [smtpPort, setSmtpPort] = useState('465');
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    loadAccounts();
  }, []);

  async function loadAccounts() {
    try {
      const response = await getEmailSettings();
      setAccounts(response.data.accounts || []);
    } catch (e) {
      console.error('Error loading accounts:', e);
    } finally {
      setLoading(false);
    }
  }

  async function handleAddAccount() {
    if (!newEmail || !newPassword) {
      toast.error('يرجى إدخال البريد وكلمة المرور');
      return;
    }

    setAdding(true);
    try {
      const response = await connectEmail({
        email_address: newEmail,
        password: newPassword,
        account_name: newAccountName || newEmail.split('@')[0],
        imap_server: imapServer,
        smtp_server: smtpServer,
        imap_port: parseInt(imapPort),
        smtp_port: parseInt(smtpPort),
        use_ssl: true,
        auto_check: true,
        check_interval: 5,
        is_primary: accounts.length === 0
      });

      if (response.data.success) {
        toast.success('تم إضافة الحساب بنجاح');
        setShowAddDialog(false);
        resetForm();
        loadAccounts();
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'فشل إضافة الحساب';
      toast.error(errorMsg);
    } finally {
      setAdding(false);
    }
  }

  async function handleDeleteAccount(accountId) {
    if (!window.confirm('هل أنت متأكد من حذف هذا الحساب؟')) {
      return;
    }

    setDeletingId(accountId);
    try {
      await deleteEmailAccount(accountId);
      toast.success('تم حذف الحساب');
      loadAccounts();
    } catch (e) {
      toast.error('فشل حذف الحساب');
    } finally {
      setDeletingId(null);
    }
  }

  async function handleSetPrimary(accountId) {
    setSettingPrimaryId(accountId);
    try {
      await setEmailPrimary(accountId);
      toast.success('تم تعيين الحساب الرئيسي');
      loadAccounts();
    } catch (e) {
      toast.error('فشل تعيين الحساب الرئيسي');
    } finally {
      setSettingPrimaryId(null);
    }
  }

  async function handleSyncAll() {
    setSyncing(true);
    try {
      const response = await syncEmails();
      toast.success(`تم مزامنة ${response.data.new_emails || 0} رسائل جديدة`);
    } catch (e) {
      toast.error('فشل المزامنة');
    } finally {
      setSyncing(false);
    }
  }

  function resetForm() {
    setNewEmail('');
    setNewPassword('');
    setNewAccountName('');
    setShowPassword(false);
    setImapServer('imap.hostinger.com');
    setSmtpServer('smtp.hostinger.com');
    setImapPort('993');
    setSmtpPort('465');
    setShowAdvanced(false);
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              حسابات البريد الإلكتروني
            </CardTitle>
            <CardDescription>Email Accounts - يمكنك ربط عدة حسابات</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncAll}
              disabled={syncing || accounts.length === 0}
            >
              {syncing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              مزامنة الكل
            </Button>
            
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-email-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  إضافة حساب
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>إضافة حساب بريد جديد</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>اسم الحساب (اختياري)</Label>
                    <Input
                      value={newAccountName}
                      onChange={(e) => setNewAccountName(e.target.value)}
                      placeholder="مثال: بريد العمل"
                      data-testid="account-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      البريد الإلكتروني *
                    </Label>
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="example@domain.com"
                      dir="ltr"
                      data-testid="new-email-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      كلمة المرور *
                    </Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="••••••••"
                        dir="ltr"
                        data-testid="new-password-input"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute left-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="text-muted-foreground"
                    >
                      <Server className="h-4 w-4 mr-2" />
                      {showAdvanced ? 'إخفاء الإعدادات المتقدمة' : 'إعدادات السيرفر المتقدمة'}
                    </Button>
                    
                    {showAdvanced && (
                      <div className="mt-4 space-y-4 p-4 rounded-lg bg-muted/30 border border-border">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs">IMAP Server</Label>
                            <Input
                              value={imapServer}
                              onChange={(e) => setImapServer(e.target.value)}
                              dir="ltr"
                              className="text-sm"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">IMAP Port</Label>
                            <Input
                              value={imapPort}
                              onChange={(e) => setImapPort(e.target.value)}
                              dir="ltr"
                              className="text-sm"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs">SMTP Server</Label>
                            <Input
                              value={smtpServer}
                              onChange={(e) => setSmtpServer(e.target.value)}
                              dir="ltr"
                              className="text-sm"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">SMTP Port</Label>
                            <Input
                              value={smtpPort}
                              onChange={(e) => setSmtpPort(e.target.value)}
                              dir="ltr"
                              className="text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAddDialog(false);
                        resetForm();
                      }}
                    >
                      إلغاء
                    </Button>
                    <Button
                      onClick={handleAddAccount}
                      disabled={adding || !newEmail || !newPassword}
                      data-testid="submit-add-email"
                    >
                      {adding ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      إضافة
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-8">
            <Mail className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">لا توجد حسابات بريد مرتبطة</p>
            <p className="text-sm text-muted-foreground">اضغط "إضافة حساب" لربط بريدك الإلكتروني</p>
          </div>
        ) : (
          <div className="space-y-3">
            {accounts.map((account) => (
              <div
                key={account.id}
                className={`p-4 rounded-lg border ${
                  account.is_primary 
                    ? 'border-primary/50 bg-primary/5' 
                    : 'border-border bg-muted/30'
                }`}
                data-testid={`email-account-${account.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      account.is_connected ? 'bg-green-500/10' : 'bg-red-500/10'
                    }`}>
                      <Mail className={`h-5 w-5 ${
                        account.is_connected ? 'text-green-500' : 'text-red-500'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{account.account_name || account.email_address.split('@')[0]}</p>
                        {account.is_primary && (
                          <Badge className="bg-primary/10 text-primary border-primary/20 text-xs">
                            <Star className="h-3 w-3 mr-1" />
                            رئيسي
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground font-mono">{account.email_address}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {account.is_connected ? (
                          <span className="text-xs text-green-500 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            متصل
                          </span>
                        ) : (
                          <span className="text-xs text-red-500 flex items-center gap-1">
                            <XCircle className="h-3 w-3" />
                            غير متصل
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          • {account.imap_server}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {!account.is_primary && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetPrimary(account.id)}
                        disabled={settingPrimaryId === account.id}
                        title="تعيين كحساب رئيسي"
                      >
                        {settingPrimaryId === account.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Star className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAccount(account.id)}
                      disabled={deletingId === account.id}
                      className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      data-testid={`delete-account-${account.id}`}
                    >
                      {deletingId === account.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Info Box */}
        <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <p className="text-sm text-muted-foreground">
            <span className="text-blue-500 font-medium">ملاحظة:</span> الحساب الرئيسي 
            <Star className="h-3 w-3 inline mx-1 text-primary" />
            يُستخدم لإرسال الرسائل من Nicole. يمكنك ربط حسابات متعددة لاستقبال الرسائل من مصادر مختلفة.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
